<template>
  <v-container class="fill-height">
    <AppToolbar></AppToolbar>
    <v-row v-if="isLoading" class="fill-height" align="center" justify="center">
      <v-progress-circular indeterminate size="50" color="primary"></v-progress-circular>
    </v-row>
    <v-row v-else-if="user" justify="center">
      <v-col cols="12" class="text-center my-3">
        <v-avatar size="150">
          <img :src="user.image ? user.image.contentUrl : require('@/assets/no-img.svg')" alt="avatar" />
        </v-avatar>
      </v-col>

      <v-col cols="12" class="text-center mb-4">
        <h1 class="display-1 mb-3">
          {{
            $t('message.welcome', {
              name: user.firstName + ' ' + user.lastName
            })
          }}
        </h1>
        <p class="subheading font-weight-regular">
          {{ $t('message.welcome_paragraph') }}
        </p>
      </v-col>

      <v-col cols="12">
        <v-row justify="center">
          <v-col cols="12" md="6">
            <v-card outlined class="fill-height">
              <v-card-title>Your account data</v-card-title>
              <v-divider></v-divider>

              <v-list two-line>
                <v-list-item>
                  <v-list-item-action>
                    <v-icon color="grey">mdi-account</v-icon>
                  </v-list-item-action>

                  <v-list-item-content>
                    <v-list-item-title>{{ user.username }}</v-list-item-title>
                    <v-list-item-subtitle>{{ $t('message.username') }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-divider inset></v-divider>

                <v-list-item>
                  <v-list-item-action>
                    <v-icon color="grey">mdi-email</v-icon>
                  </v-list-item-action>

                  <v-list-item-content>
                    <v-list-item-title>{{ user.email }}</v-list-item-title>
                    <v-list-item-subtitle>{{ $t('message.email') }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-divider inset></v-divider>

                <v-list-item>
                  <v-list-item-action>
                    <v-icon color="grey">mdi-translate</v-icon>
                  </v-list-item-action>

                  <v-list-item-content>
                    <v-list-item-title>{{
                      user.locale === 'es' ? $t('message.spanish') : $t('message.english')
                    }}</v-list-item-title>
                    <v-list-item-subtitle>{{ $t('message.language') }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>

              <v-divider class="mt-2"></v-divider>

              <template v-if="user.roleInOrganizations.length">
                <v-list subheader two-line>
                  <v-subheader>{{ $t('message.role_in_organizations') }}</v-subheader>

                  <template v-for="(roleInOrganization, index) in user.roleInOrganizations">
                    <v-divider v-if="index !== 0" :key="index" inset></v-divider>
                    <v-list-item :key="roleInOrganization['@id']">
                      <v-list-item-avatar>
                        <img
                          :src="
                            roleInOrganization.organization.image
                              ? roleInOrganization.organization.image.contentUrl + '?x-request=html'
                              : require('@/assets/no-img.svg')
                          "
                        />
                      </v-list-item-avatar>

                      <v-list-item-content>
                        <v-list-item-title>{{ roleInOrganization.organization.name }}</v-list-item-title>
                        <v-list-item-subtitle>{{
                          $t('message.' + roleInOrganization.organizationRole.name)
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </template>

              <template v-if="user.roleInVessels.length">
                <v-divider></v-divider>
                <v-list subheader two-line>
                  <v-subheader>{{ $t('message.role_in_vessels') }}</v-subheader>

                  <template v-for="(roleInVessel, index) in user.roleInVessels">
                    <v-divider v-if="index !== 0" :key="index" inset></v-divider>
                    <v-list-item :key="roleInVessel['@id']">
                      <v-list-item-avatar>
                        <img
                          :src="
                            roleInVessel.vessel.image
                              ? roleInVessel.vessel.image.contentUrl + '?x-request=html'
                              : require('@/assets/no-img.svg')
                          "
                        />
                      </v-list-item-avatar>

                      <v-list-item-content>
                        <v-list-item-title>{{ roleInVessel.vessel.name }}</v-list-item-title>
                        <v-list-item-subtitle>{{ $t('message.' + roleInVessel.vesselRole.name) }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </template>

              <template v-if="this.$usailSecurity.hasReachableRole('ROLE_UPDATE_USERS')">
                <v-divider></v-divider>

                <v-card-actions>
                  <v-btn text color="blue" :to="{ name: 'userEdit' }">{{ $t('message.edit_account_data') }}</v-btn>
                </v-card-actions>
              </template>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="fill-height">
              <v-card-title>USAIL apps</v-card-title>
              <v-divider></v-divider>
              <template v-if="isProteusAllowed">
                <v-card-text>
                  <img
                    class="mb-3"
                    src="https://file.usail.es/static/proteus-logo-black.svg"
                    alt="USAIL Proteus"
                    :style="{ height: appLogoHeight }"
                  />
                  <div class="subtitle-1">
                    {{ $t('message.proteus_paragraph') }}
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-btn :href="proteusUrl" text color="blue" class="mb-3">{{ $t('message.access_proteus') }}</v-btn>
                </v-card-actions>
              </template>
              <template v-if="isConnectAllowed">
                <v-card-text>
                  <img
                    class="mb-3"
                    src="https://file.usail.es/static/connect-logo-black.svg"
                    alt="USAIL Connect"
                    :style="{ height: appLogoHeight }"
                  />
                  <div class="subtitle-1">
                    {{ $t('message.connect_paragraph') }}
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-btn :href="connectUrl" text color="blue" class="mb-3">{{ $t('message.access_connect') }}</v-btn>
                </v-card-actions>
              </template>

              <template v-if="isFleetCommanderAllowed">
                <v-divider></v-divider>
                <v-card-text>
                  <img
                    class="mb-3"
                    src="https://file.usail.es/static/fleet-commander-logo-black.svg"
                    alt="USAIL Connect"
                    :style="{ height: appLogoHeight }"
                  />
                  <div class="subtitle-1">
                    {{ $t('message.fleet_commander_paragraph') }}
                  </div>
                </v-card-text>

                <v-card-actions>
                  <v-btn :href="fleetUrl" text color="blue" class="mb-3">{{
                    $t('message.access_fleet_commander')
                  }}</v-btn>
                </v-card-actions>
              </template>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import AppToolbar from '@/components/AppToolbar'

export default {
  name: 'Home',

  components: {
    AppToolbar
  },

  data() {
    return {
      dialog: false,
      connectUrl: process.env.VUE_APP_CONNECT_HOST,
      fleetUrl: process.env.VUE_APP_FLEET_COMMANDER_HOST,
      proteusUrl: process.env.VUE_APP_PROTEUS_HOST
    }
  },

  computed: {
    ...mapFields('user', {
      isLoading: 'isLoading'
    }),
    ...mapGetters('user', ['find']),

    user() {
      let userInfo = this.$usailSecurity.getUser()
      if (!userInfo) {
        return
      }
      return this.find(`/users/${userInfo.sub}`)
    },

    isConnectAllowed() {
      return this.$usailSecurity.hasRoleInOrganizations(['ROLE_SHIPOWNER', 'ROLE_SHORE_STAFF'])
    },

    isFleetCommanderAllowed() {
      return (
        this.$usailSecurity.hasRoleInOrganizations(['ROLE_SHIPOWNER', 'ROLE_SHORE_STAFF']) ||
        this.$usailSecurity.hasRoleInVessels(['ROLE_CAPTAIN', 'ROLE_FLEET_MANAGER'])
      )
    },

    isProteusAllowed() {
      return (
        this.$usailSecurity.hasReachableRole('ROLE_GOD') ||
        this.$usailSecurity.hasReachableRole('ROLE_STAFF') ||
        this.$usailSecurity.hasReachableRole('ROLE_DEALER') ||
        this.$usailSecurity.hasReachableRole('ROLE_NOC_EXTERNAL')
      )
    },

    appLogoHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return '20px'
        case 'sm':
          return '20px'
        case 'md':
          return '30px'
        case 'lg':
          return '33px'
        case 'xl':
          return '33px'
        default:
          return '20px'
      }
    }
  },

  created() {
    let userInfo = this.$usailSecurity.getUser()
    if (userInfo) {
      this.retrieve('/users/' + userInfo.sub)
    }
  },

  methods: {
    ...mapActions('user', {
      deleteItem: 'del',
      reset: 'reset',
      retrieve: 'load'
    })
  }
}
</script>
